import type { FileNode } from "./types";
import { icon } from "./icons";
import { store } from "./store";

const expanded = new Set<string>();

function renderNodes(nodes: FileNode[], depth: number, activePath: string | null): HTMLElement {
  const wrap = document.createElement("div");

  for (const node of nodes) {
    const row = document.createElement("div");
    const isExpanded = expanded.has(node.path);
    const isActive = node.path === activePath;

    row.className = `group flex items-center gap-1.5 py-2 pr-2 text-sm ${
      isActive ? "bg-white/10 text-foreground" : "text-foreground/90"
    }`;
    row.style.paddingLeft = `${depth * 14 + 10}px`;

    const openBtn = document.createElement("button");
    openBtn.type = "button";
    openBtn.className = "flex min-w-0 flex-1 items-center gap-1.5 text-left";
    const iconHtml =
      node.type === "directory"
        ? icon(isExpanded ? "folderOpen" : "folder", "size-4 shrink-0 text-muted-foreground")
        : icon("file", "size-4 shrink-0 text-muted-foreground");
    const nameSpan = document.createElement("span");
    nameSpan.className = "truncate";
    nameSpan.textContent = node.name;
    openBtn.innerHTML = iconHtml;
    openBtn.appendChild(nameSpan);
    openBtn.addEventListener("click", () => {
      if (node.type === "directory") {
        if (expanded.has(node.path)) expanded.delete(node.path);
        else expanded.add(node.path);
        renderExplorer();
      } else {
        store.getState().openFile(node.path);
      }
    });

    const deleteBtn = document.createElement("button");
    deleteBtn.type = "button";
    deleteBtn.setAttribute("aria-label", `Delete ${node.name}`);
    deleteBtn.className =
      "shrink-0 rounded p-1.5 text-muted-foreground hover:bg-white/10 hover:text-red-400 active:bg-white/10 active:text-red-400";
    deleteBtn.innerHTML = icon("trash2", "size-3.5");
    deleteBtn.addEventListener("click", async () => {
      if (window.confirm(`Delete "${node.path}"? This cannot be undone.`)) {
        await store.getState().deleteEntry(node.path);
      }
    });

    row.appendChild(openBtn);
    row.appendChild(deleteBtn);
    wrap.appendChild(row);

    if (node.type === "directory" && isExpanded && node.children) {
      wrap.appendChild(renderNodes(node.children, depth + 1, activePath));
    }
  }

  return wrap;
}

export function renderExplorer() {
  const treeContainer = document.getElementById("file-tree");
  if (!treeContainer) return;

  const { tree, activePath } = store.getState();
  treeContainer.innerHTML = "";

  if (tree.length === 0) {
    const empty = document.createElement("p");
    empty.className = "px-3 py-4 text-sm text-muted-foreground";
    empty.textContent = "Workspace is empty. Create a file to get started.";
    treeContainer.appendChild(empty);
    return;
  }

  treeContainer.appendChild(renderNodes(tree, 0, activePath));
}

export function initExplorer() {
  const backdrop = document.getElementById("sheet-backdrop")!;
  const panel = document.getElementById("sheet-panel")!;
  const closeBtn = document.getElementById("explorer-close")!;
  const newPathInput = document.getElementById("new-entry-path") as HTMLInputElement;
  const newFileBtn = document.getElementById("new-entry-file")!;
  const newFolderBtn = document.getElementById("new-entry-folder")!;

  const close = () => store.getState().setExplorerOpen(false);

  closeBtn.addEventListener("click", close);
  backdrop.addEventListener("click", close);
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && store.getState().explorerOpen) close();
  });

  const handleCreate = async (type: "file" | "directory") => {
    const value = newPathInput.value.trim();
    if (!value) return;
    await store.getState().createEntry(value, type);
    newPathInput.value = "";
  };

  newFileBtn.addEventListener("click", () => handleCreate("file"));
  newFolderBtn.addEventListener("click", () => handleCreate("directory"));

  store.subscribe((state, prev) => {
    if (state.explorerOpen !== prev.explorerOpen) {
      backdrop.classList.toggle("open", state.explorerOpen);
      panel.classList.toggle("open", state.explorerOpen);
    }
    if (state.tree !== prev.tree || state.activePath !== prev.activePath) {
      renderExplorer();
    }
  });

  renderExplorer();
}
